import React, { useState } from 'react';
import { Activity, FileHeart, Plus, Siren, Trash2, Users } from 'lucide-react';
import ReadinessCard, { FieldError, SummaryRow, inputClass, labelClass } from './ReadinessCard';
import {
  isMinor,
  saveErrorToFields,
  validateAdvanceDirective,
  validateEmergencyPlan,
  validateServices,
  validateThsteps,
} from './readinessValidation';
import type { FieldErrors } from './readinessValidation';
import {
  ADVANCE_DIRECTIVE_LABELS,
  ADVANCE_DIRECTIVE_STATUSES,
  IMMUNIZATION_STATUS_LABELS,
  OTHER_SERVICE_TYPES,
  TRIAGE_CATEGORIES,
} from '../../../../dtos/patientmr/socItems.dto';
import type {
  AdvanceDirectiveItemInput,
  AdvanceDirectiveStatus,
  EmergencyPlanItemInput,
  ImmunizationStatus,
  OtherServiceType,
  ReadinessItemName,
  ServicesItemInput,
  SocItemInputMap,
  SocItemsResponse,
  ThstepsItemInput,
  TriageCategory,
} from '../../../../dtos/patientmr/socItems.dto';

export type ReadinessSave = <K extends ReadinessItemName | 'emergency-plan'>(item: K, input: SocItemInputMap[K]) => Promise<void>;

export interface ReadinessCardBaseProps {
  record: SocItemsResponse;
  canEdit: boolean;
  onSave: ReadinessSave;
}

const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const [y, m, d] = value.slice(0, 10).split('-');
  return y && m && d ? `${m}/${d}/${y}` : value;
};
const yesNo = (v: boolean | null | undefined): string => (v === true ? 'Yes' : v === false ? 'No' : '');
const blankToNull = (v: string): string | null => (v.trim() ? v.trim() : null);

const notRecorded = <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">Not recorded</span>;

/** Editing state shared by the cards: draft, client + server field errors, saving */
function useEditor<T>(fromRecord: () => T) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<T>(fromRecord);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const open = () => { setDraft(fromRecord()); setErrors({}); setEditing(true); };
  const cancel = () => { setEditing(false); setErrors({}); };
  const run = async (clientErrors: FieldErrors, save: () => Promise<void>) => {
    if (Object.keys(clientErrors).length) { setErrors(clientErrors); return; }
    setSaving(true);
    setErrors({});
    try {
      await save();
      setEditing(false);
    } catch (err) {
      setErrors(saveErrorToFields(err));
    } finally {
      setSaving(false);
    }
  };
  const set = <K extends keyof T>(key: K, value: T[K]) => setDraft((d) => ({ ...d, [key]: value }));
  return { editing, draft, set, setDraft, errors, saving, open, cancel, run };
}

// ─── Advance directive ────────────────────────────────────────────────────────

interface AdDraft { adStatus: AdvanceDirectiveStatus | ''; adFormDate: string; adPhysician: string; adTerminalDxAttested: boolean; adNote: string }

export const AdvanceDirectiveCard: React.FC<ReadinessCardBaseProps & { patientDob?: string }> = ({ record, canEdit, onSave, patientDob }) => {
  const ed = useEditor<AdDraft>(() => ({
    adStatus: record.adStatus ?? '',
    adFormDate: record.adFormDate ?? '',
    adPhysician: record.adPhysician ?? '',
    adTerminalDxAttested: record.adTerminalDxAttested === true,
    adNote: record.adNote ?? '',
  }));
  const minor = isMinor(patientDob);
  const isDnr = ed.draft.adStatus === 'OOH_DNR';

  const save = () => {
    if (!ed.draft.adStatus) { void ed.run({ adStatus: 'Choose the advance directive status.' }, async () => undefined); return; }
    const input: AdvanceDirectiveItemInput = {
      adStatus: ed.draft.adStatus,
      adFormDate: blankToNull(ed.draft.adFormDate),
      adPhysician: blankToNull(ed.draft.adPhysician),
      adNote: blankToNull(ed.draft.adNote),
      ...(isDnr ? { adTerminalDxAttested: ed.draft.adTerminalDxAttested } : {}),
    };
    void ed.run(validateAdvanceDirective(input, patientDob), () => onSave('advance-directive', input));
  };

  return (
    <ReadinessCard
      title="Advance directive"
      icon={<FileHeart size={16} />}
      status={!record.adStatus ? notRecorded : record.adStatus === 'OOH_DNR'
        ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">OOH-DNR</span> : undefined}
      editing={ed.editing}
      canEdit={canEdit}
      saving={ed.saving}
      errors={ed.errors}
      onEdit={ed.open}
      onCancel={ed.cancel}
      onSave={save}
      summary={(
        <>
          <SummaryRow label="Status" value={record.adStatus ? ADVANCE_DIRECTIVE_LABELS[record.adStatus] : ''} />
          <SummaryRow label="Form date" value={formatDate(record.adFormDate)} />
          <SummaryRow label="Physician" value={record.adPhysician} />
          {record.adStatus === 'OOH_DNR' && <SummaryRow label="Terminal condition attested" value={yesNo(record.adTerminalDxAttested)} />}
          {record.adNote && <SummaryRow label="Note" value={record.adNote} />}
        </>
      )}
      form={(
        <>
          <label className={labelClass}>
            Status
            <select aria-label="Advance directive status" value={ed.draft.adStatus} onChange={(e) => ed.set('adStatus', e.target.value as AdvanceDirectiveStatus | '')} className={inputClass}>
              <option value="">Choose…</option>
              {ADVANCE_DIRECTIVE_STATUSES.map((s) => <option key={s} value={s}>{ADVANCE_DIRECTIVE_LABELS[s]}</option>)}
            </select>
            <FieldError errors={ed.errors} field="adStatus" />
          </label>
          {ed.draft.adStatus && ed.draft.adStatus !== 'NONE' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className={labelClass}>
                Form date{isDnr && <span className="text-red-600" aria-hidden="true"> *</span>}
                <input aria-label="Form date" type="date" value={ed.draft.adFormDate} onChange={(e) => ed.set('adFormDate', e.target.value)} className={inputClass} />
                <FieldError errors={ed.errors} field="adFormDate" />
              </label>
              <label className={labelClass}>
                Physician
                <input aria-label="Physician" type="text" maxLength={120} value={ed.draft.adPhysician} onChange={(e) => ed.set('adPhysician', e.target.value)} className={inputClass} />
              </label>
            </div>
          )}
          {isDnr && (
            <div>
              <label className="flex min-h-[44px] items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5"
                  checked={ed.draft.adTerminalDxAttested}
                  onChange={(e) => ed.set('adTerminalDxAttested', e.target.checked)}
                  aria-label="Terminal or irreversible condition attested"
                />
                <span>
                  A physician has diagnosed a terminal or irreversible condition
                  {minor && <span className="block text-xs text-slate-500">Required for a patient under 18 (HSC §166.085).</span>}
                </span>
              </label>
              <FieldError errors={ed.errors} field="adTerminalDxAttested" />
            </div>
          )}
          <label className={labelClass}>
            Note
            <input aria-label="Advance directive note" type="text" maxLength={500} value={ed.draft.adNote} onChange={(e) => ed.set('adNote', e.target.value)} className={inputClass} />
          </label>
        </>
      )}
    />
  );
};

// ─── Emergency plan ───────────────────────────────────────────────────────────

interface EpDraft {
  emergencyTriageCategory: TriageCategory | '';
  evacuationAssistance: boolean;
  stearRegistered: boolean;
  backupPower: string;
  equipmentVendorName: string;
  equipmentVendorPhone: string;
  emergencyHospital: string;
  emergencyContingencyPlan: string;
}

export const EmergencyPlanCard: React.FC<ReadinessCardBaseProps> = ({ record, canEdit, onSave }) => {
  const ed = useEditor<EpDraft>(() => ({
    emergencyTriageCategory: record.emergencyTriageCategory ?? '',
    evacuationAssistance: record.evacuationAssistance === true,
    stearRegistered: record.stearRegistered === true,
    backupPower: record.backupPower ?? '',
    equipmentVendorName: record.equipmentVendorName ?? '',
    equipmentVendorPhone: record.equipmentVendorPhone ?? '',
    emergencyHospital: record.emergencyHospital ?? '',
    emergencyContingencyPlan: record.emergencyContingencyPlan ?? '',
  }));

  const save = () => {
    const d = ed.draft;
    const input: EmergencyPlanItemInput = {
      emergencyTriageCategory: d.emergencyTriageCategory || null,
      evacuationAssistance: d.evacuationAssistance,
      stearRegistered: d.stearRegistered,
      backupPower: d.backupPower.trim(),
      equipmentVendorName: d.equipmentVendorName.trim(),
      equipmentVendorPhone: d.equipmentVendorPhone.trim(),
      emergencyHospital: d.emergencyHospital.trim(),
      emergencyContingencyPlan: d.emergencyContingencyPlan.trim(),
    };
    void ed.run(validateEmergencyPlan(input), () => onSave('emergency-plan', input));
  };

  const text = (key: 'backupPower' | 'equipmentVendorName' | 'equipmentVendorPhone' | 'emergencyHospital', label: string, max: number, type = 'text') => (
    <label className={labelClass}>
      {label}
      <input aria-label={label} type={type} maxLength={max} value={ed.draft[key]} onChange={(e) => ed.set(key, e.target.value)} className={inputClass} />
      <FieldError errors={ed.errors} field={key} />
    </label>
  );

  return (
    <ReadinessCard
      title="Emergency plan"
      icon={<Siren size={16} />}
      status={!record.emergencyTriageCategory ? notRecorded : undefined}
      editing={ed.editing}
      canEdit={canEdit}
      saving={ed.saving}
      errors={ed.errors}
      onEdit={ed.open}
      onCancel={ed.cancel}
      onSave={save}
      summary={(
        <>
          <SummaryRow label="Plan on file" value={record.emergencyPlanOnFile ? `Yes${record.emergencyPlanDate ? `, ${formatDate(record.emergencyPlanDate)}` : ''}` : 'No'} />
          <SummaryRow label="Triage category" value={record.emergencyTriageCategory} />
          <SummaryRow label="Needs evacuation help" value={yesNo(record.evacuationAssistance)} />
          <SummaryRow label="STEAR registered" value={yesNo(record.stearRegistered)} />
          <SummaryRow label="Backup power" value={record.backupPower} />
          <SummaryRow label="Equipment vendor" value={[record.equipmentVendorName, record.equipmentVendorPhone].filter(Boolean).join(' · ')} />
          <SummaryRow label="Hospital" value={record.emergencyHospital} />
        </>
      )}
      form={(
        <>
          <p className="text-xs text-slate-500">Marking the plan on file stays in the SOC checklist.</p>
          <label className={labelClass}>
            Triage category
            <select aria-label="Triage category" value={ed.draft.emergencyTriageCategory} onChange={(e) => ed.set('emergencyTriageCategory', e.target.value as TriageCategory | '')} className={inputClass}>
              <option value="">Not set</option>
              {TRIAGE_CATEGORIES.map((c) => <option key={c} value={c}>Category {c}</option>)}
            </select>
            <FieldError errors={ed.errors} field="emergencyTriageCategory" />
          </label>
          <div className="flex flex-wrap gap-x-6">
            <label className="flex min-h-[44px] items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-5 w-5" checked={ed.draft.evacuationAssistance} onChange={(e) => ed.set('evacuationAssistance', e.target.checked)} />
              Needs help to evacuate
            </label>
            <label className="flex min-h-[44px] items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-5 w-5" checked={ed.draft.stearRegistered} onChange={(e) => ed.set('stearRegistered', e.target.checked)} />
              Registered with STEAR
            </label>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {text('backupPower', 'Backup power', 300)}
            {text('emergencyHospital', 'Emergency hospital', 200)}
            {text('equipmentVendorName', 'Equipment vendor', 120)}
            {text('equipmentVendorPhone', 'Vendor phone', 30, 'tel')}
          </div>
          <label className={labelClass}>
            Contingency plan
            <textarea aria-label="Contingency plan" rows={3} maxLength={1000} value={ed.draft.emergencyContingencyPlan} onChange={(e) => ed.set('emergencyContingencyPlan', e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <FieldError errors={ed.errors} field="emergencyContingencyPlan" />
          </label>
        </>
      )}
    />
  );
};

// ─── THSteps ──────────────────────────────────────────────────────────────────

interface ThDraft { thstepsLastCheckup: string; immunizationStatus: ImmunizationStatus | ''; thstepsNote: string }

export const ThstepsCard: React.FC<ReadinessCardBaseProps> = ({ record, canEdit, onSave }) => {
  const ed = useEditor<ThDraft>(() => ({
    thstepsLastCheckup: record.thstepsLastCheckup ?? '',
    immunizationStatus: record.immunizationStatus ?? '',
    thstepsNote: record.thstepsNote ?? '',
  }));
  const overdue = !!record.thstepsNextDue && record.thstepsNextDue < new Date().toISOString().slice(0, 10);

  const save = () => {
    const d = ed.draft;
    const input: ThstepsItemInput = {
      thstepsLastCheckup: blankToNull(d.thstepsLastCheckup),
      ...(d.immunizationStatus ? { immunizationStatus: d.immunizationStatus } : {}),
      thstepsNote: blankToNull(d.thstepsNote),
    };
    void ed.run(validateThsteps(input), () => onSave('thsteps', input));
  };

  return (
    <ReadinessCard
      title="Texas Health Steps"
      icon={<Activity size={16} />}
      status={!record.thstepsLastCheckup ? notRecorded : overdue
        ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Checkup due</span> : undefined}
      editing={ed.editing}
      canEdit={canEdit}
      saving={ed.saving}
      errors={ed.errors}
      onEdit={ed.open}
      onCancel={ed.cancel}
      onSave={save}
      summary={(
        <>
          <SummaryRow label="Last checkup" value={formatDate(record.thstepsLastCheckup)} />
          <SummaryRow label="Next due" value={formatDate(record.thstepsNextDue)} />
          <SummaryRow label="Immunizations" value={record.immunizationStatus ? IMMUNIZATION_STATUS_LABELS[record.immunizationStatus] : ''} />
          {record.thstepsNote && <SummaryRow label="Note" value={record.thstepsNote} />}
        </>
      )}
      form={(
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={labelClass}>
              Last checkup
              <input aria-label="Last checkup" type="date" value={ed.draft.thstepsLastCheckup} onChange={(e) => ed.set('thstepsLastCheckup', e.target.value)} className={inputClass} />
              <FieldError errors={ed.errors} field="thstepsLastCheckup" />
            </label>
            <label className={labelClass}>
              Immunizations
              <select aria-label="Immunization status" value={ed.draft.immunizationStatus} onChange={(e) => ed.set('immunizationStatus', e.target.value as ImmunizationStatus | '')} className={inputClass}>
                <option value="">Not set</option>
                {(Object.keys(IMMUNIZATION_STATUS_LABELS) as ImmunizationStatus[]).map((s) => <option key={s} value={s}>{IMMUNIZATION_STATUS_LABELS[s]}</option>)}
              </select>
              <FieldError errors={ed.errors} field="immunizationStatus" />
            </label>
          </div>
          <label className={labelClass}>
            Note
            <input aria-label="THSteps note" type="text" maxLength={500} value={ed.draft.thstepsNote} onChange={(e) => ed.set('thstepsNote', e.target.value)} className={inputClass} />
          </label>
          <p className="text-xs text-slate-500">The next due date is calculated from the date of birth and the last checkup.</p>
        </>
      )}
    />
  );
};

// ─── Other services ───────────────────────────────────────────────────────────

interface SvcRow { type: OtherServiceType | ''; provider: string; hoursPerWeek: string }
interface SvcDraft { rows: SvcRow[]; ispDate: string; serviceCoordinatorName: string; serviceCoordinatorPhone: string }

export const OtherServicesCard: React.FC<ReadinessCardBaseProps> = ({ record, canEdit, onSave }) => {
  const ed = useEditor<SvcDraft>(() => ({
    rows: (record.otherServices || []).map((s) => ({ type: s.type, provider: s.provider || '', hoursPerWeek: s.hoursPerWeek === null ? '' : String(s.hoursPerWeek) })),
    ispDate: record.ispDate ?? '',
    serviceCoordinatorName: record.serviceCoordinatorName ?? '',
    serviceCoordinatorPhone: record.serviceCoordinatorPhone ?? '',
  }));
  const setRow = (i: number, patch: Partial<SvcRow>) =>
    ed.set('rows', ed.draft.rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = () => {
    const d = ed.draft;
    const input: ServicesItemInput = {
      otherServices: d.rows.map((r) => ({
        type: r.type as OtherServiceType,
        provider: r.provider.trim(),
        hoursPerWeek: r.hoursPerWeek.trim() === '' ? null : Number(r.hoursPerWeek),
      })),
      ispDate: blankToNull(d.ispDate),
      serviceCoordinatorName: blankToNull(d.serviceCoordinatorName),
      serviceCoordinatorPhone: blankToNull(d.serviceCoordinatorPhone),
    };
    void ed.run(validateServices(input), () => onSave('services', input));
  };

  const services = record.otherServices || [];
  return (
    <ReadinessCard
      title="Other services"
      icon={<Users size={16} />}
      status={!services.length && !record.serviceCoordinatorName ? notRecorded : undefined}
      editing={ed.editing}
      canEdit={canEdit}
      saving={ed.saving}
      errors={ed.errors}
      onEdit={ed.open}
      onCancel={ed.cancel}
      onSave={save}
      summary={(
        <>
          {services.length === 0 && <SummaryRow label="Services" value="None recorded" />}
          {services.map((s, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <SummaryRow key={`${s.type}-${i}`} label={s.type} value={[s.provider, s.hoursPerWeek !== null ? `${s.hoursPerWeek} h/wk` : ''].filter(Boolean).join(' · ')} />
          ))}
          <SummaryRow label="ISP date" value={formatDate(record.ispDate)} />
          <SummaryRow label="Service coordinator" value={[record.serviceCoordinatorName, record.serviceCoordinatorPhone].filter(Boolean).join(' · ')} />
        </>
      )}
      form={(
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-slate-600">Services the patient receives from others</legend>
            {ed.draft.rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="grid grid-cols-[1fr_1fr_5rem_auto] items-start gap-2">
                <label className={labelClass}>
                  <span className="sr-only">Service type {i + 1}</span>
                  <select aria-label={`Service type ${i + 1}`} value={r.type} onChange={(e) => setRow(i, { type: e.target.value as OtherServiceType | '' })} className={inputClass}>
                    <option value="">Type…</option>
                    {OTHER_SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <FieldError errors={ed.errors} field={`otherServices[${i}].type`} />
                </label>
                <label className={labelClass}>
                  <span className="sr-only">Provider {i + 1}</span>
                  <input aria-label={`Provider ${i + 1}`} placeholder="Provider" maxLength={120} value={r.provider} onChange={(e) => setRow(i, { provider: e.target.value })} className={inputClass} />
                </label>
                <label className={labelClass}>
                  <span className="sr-only">Hours per week {i + 1}</span>
                  <input aria-label={`Hours per week ${i + 1}`} placeholder="h/wk" inputMode="decimal" value={r.hoursPerWeek} onChange={(e) => setRow(i, { hoursPerWeek: e.target.value })} className={inputClass} />
                  <FieldError errors={ed.errors} field={`otherServices[${i}].hoursPerWeek`} />
                </label>
                <button type="button" onClick={() => ed.set('rows', ed.draft.rows.filter((_, idx) => idx !== i))} className="mt-1 flex h-11 w-11 items-center justify-center rounded-md text-red-600 hover:bg-red-50" aria-label={`Remove service ${i + 1}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <FieldError errors={ed.errors} field="otherServices" />
            {ed.draft.rows.length < 20 && (
              <button type="button" onClick={() => ed.set('rows', [...ed.draft.rows, { type: '', provider: '', hoursPerWeek: '' }])} className="flex min-h-[44px] items-center gap-1 text-sm font-medium text-slate-700">
                <Plus size={16} />Add service
              </button>
            )}
          </fieldset>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className={labelClass}>
              ISP date
              <input aria-label="ISP date" type="date" value={ed.draft.ispDate} onChange={(e) => ed.set('ispDate', e.target.value)} className={inputClass} />
              <FieldError errors={ed.errors} field="ispDate" />
            </label>
            <label className={labelClass}>
              Service coordinator
              <input aria-label="Service coordinator" maxLength={120} value={ed.draft.serviceCoordinatorName} onChange={(e) => ed.set('serviceCoordinatorName', e.target.value)} className={inputClass} />
            </label>
            <label className={labelClass}>
              Coordinator phone
              <input aria-label="Coordinator phone" type="tel" maxLength={30} value={ed.draft.serviceCoordinatorPhone} onChange={(e) => ed.set('serviceCoordinatorPhone', e.target.value)} className={inputClass} />
            </label>
          </div>
        </>
      )}
    />
  );
};
